<template>
  <div class="trace-grid-wrap d-flex flex-column overflow-hidden">
    <div class="grid-host d-flex flex-column">
      <div class="trace-page">
        <div class="grid">
          <!-- Left column -->
          <LeftPanel
            class="column left"
            :steps-list="stepsList"
            :steps-completed="stepsCompleted"
            :steps-total="taskStepsCount"
            :steps-time="stepsTime"
            :substep-metrics="substepMetrics"
          />
          <!-- Middle column -->
          <div class="column middle">
            <InfoCard tone="success" title="Current Step">
              <template #default>
                <div
                  v-for="currentStep in currentStepsText"
                  :key="currentStep"
                  class="body-lg"
                >
                  {{ currentStep }}
                </div>
              </template>
            </InfoCard>

            <InfoCard tone="info" title="Next Step">
              <template #default>
                <div class="body-lg">{{ nextStepText }}</div>
              </template>
            </InfoCard>

            <InfoCard
              tone="danger"
              :title="`Potential Issues: (${potentialIssuesText.length})`"
            >
              <template #default>
                <div
                  v-for="potentialIssue in potentialIssuesText"
                  :key="potentialIssue"
                  class="body-lg"
                >
                  {{ potentialIssue }}
                </div>
              </template>
            </InfoCard>
          </div>

          <!-- Right column -->
          <div class="column right">
            <VideoPanel
              :video-url="video.fileURL"
              @ready="
                (el) => {
                  attachVideo(el);
                  onLoad();
                }
              "
              @play="onPlay"
              @pause="onPause"
              @seeking="handleSeeking"
              @ended="onEnd"
            />
            <TimeStats
              :cycle-time="cycleTime"
              :background-time="backgroundTime"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useTraceCycle } from 'src/composables/useTraceCycle';
import InfoCard from './components/InfoCard.vue';
import LeftPanel from './components/LeftPanel.vue';
import TimeStats from './components/TimeStats.vue';
import VideoPanel from './components/VideoPanel.vue';

export default {
  name: 'TraceDashboard',
  components: { LeftPanel, InfoCard, VideoPanel, TimeStats },
  props: {
    navigate: { type: Boolean, default: false },
    isFetchingVideoUrl: { type: Boolean, default: false },
    video: { type: Object, default: () => ({}) },
    stepsList: { type: Array, default: () => [] },
    comments: { type: Array, default: () => [] },
    isVisualization: { type: Boolean, default: false },
    showBoundingBoxes: { type: Boolean, default: false },
  },

  setup(props, { emit }) {
    const {
      stepsTime,
      currentStepsText,
      nextStepText,
      potentialIssuesText,
      frame,
      onLoad,
      onPlay,
      onPause,
      onEnd,
      handleSeeking,
      attachVideo,
      cycleTime,
      backgroundTime,
      isPredictionFileNotExist,
      taskStepsCount,
      stepsCompleted,
      substepMetrics,
    } = useTraceCycle({ props, emit });

    return {
      stepsTime,
      currentStepsText,
      nextStepText,
      potentialIssuesText,
      frame,
      onLoad,
      onPlay,
      onPause,
      onEnd,
      handleSeeking,
      attachVideo,
      cycleTime,
      backgroundTime,
      isPredictionFileNotExist,
      taskStepsCount,
      stepsCompleted,
      substepMetrics,
    };
  },
};
</script>

<style>
/* Fluid type & spacing */
:root {
  --gap: clamp(8px, 1vw, 16px);
  --radius: 12px;
  --card-pad: clamp(10px, 1.2vw, 16px);
  /* Fonts shrink more on narrow screens to keep 3-column layout usable */
  --fs-xs: clamp(10px, 0.95vw, 13px);
  --fs-sm: clamp(11px, 1vw, 14px);
  --fs-md: clamp(12px, 1.05vw, 16px);
  --fs-lg: clamp(14px, 1.4vw, 20px);
  --fs-h: clamp(16px, 1.6vw, 24px);
}

/* Global sizing + margin reset to avoid overflow from borders/padding */
*,
*::before,
*::after {
  box-sizing: border-box;
}
html,
body,
#app {
  height: 100%;
}
body {
  margin: 0;
}

.trace-grid-wrap {
  flex: 1 1 auto;
  min-height: 0; /* allow grid to size within modal without page scroll */
}
.grid-host {
  flex: 1 1 auto;
  min-height: 0;
}

.trace-page {
  inline-size: 100%;
  block-size: 100%; /* fill parent (modal body) */
  min-height: 0; /* allow children to shrink inside modal */
}

.grid {
  display: grid;
  /* Left ~32% | Middle 25% | Right ~43% */
  grid-template-columns: 1.3fr 1fr 1.7fr;
  gap: var(--gap);

  inline-size: 100%;
  height: 100%; /* fill modal body height */
  box-sizing: border-box;
  align-items: stretch; /* stretch children to full height */
  justify-items: stretch; /* stretch children to full width */
  overflow: hidden; /* prevent page scroll; inner areas manage overflow */
  background: transparent;
}

.column {
  min-height: 0; /* enables inner scroll areas */
  height: 100%;
  width: 100%;
}

/* Middle column is 3 stacked info cards */
.middle {
  display: grid;
  gap: var(--gap);
  /* Use fr units so gaps are accounted for and no overflow occurs */
  grid-template-rows: 1fr 1fr 2fr;
  height: 100%;
  min-height: 0;
  overflow: hidden; /* avoid column scrollbar; cards handle overflow */
}

/* Right column is video, stats, actions */
.right {
  display: grid;
  gap: var(--gap);
  /* Make the top block flexible, and allow it to shrink below content */
  grid-template-rows: minmax(0, 1fr) auto;
  align-content: stretch;
  height: 100%;
  min-height: 0;
  overflow: hidden; /* avoid scrollbar gutter changing visual gap */
}

/* Typography helpers */
.body-sm {
  font-size: var(--fs-sm);
}
.body-lg {
  font-size: var(--fs-md);
}

/* Keep 3 columns at all sizes; only Typography/spacing respond */
@media (max-width: 1100px) {
  :root {
    --card-pad: clamp(8px, 1vw, 14px);
    --gap: clamp(8px, 1vw, 14px);
    --fs-h: clamp(15px, 1.5vw, 22px);
    --fs-md: clamp(12px, 1vw, 15px);
    --fs-sm: clamp(11px, 0.95vw, 13px);
  }
}
@media (max-width: 860px) {
  :root {
    --card-pad: clamp(6px, 0.9vw, 12px);
    --gap: clamp(6px, 0.9vw, 12px);
    --fs-h: clamp(14px, 1.4vw, 20px);
    --fs-md: clamp(11px, 0.95vw, 14px);
    --fs-sm: clamp(10px, 0.9vw, 12px);
  }
}
</style>
